import React from "react";
import classNames from "classnames";
import AsyncSelect from "react-select/async";
import FormGroup from "../FormGroup.js";
import FormLabel from "../FormLabel.js";
import FormInputGroup from "../FormInputGroup.js";
import { MultiValue, SingleValue } from "react-select";
import { useLazyFetchMailFoldersForUserQuery } from "../../../services/useremails";
import { MailMessageFolder } from "../../../types/MailMessageFolder";

export type UserMailboxSelectProps = {
    className?: any
    error?: Error | string | undefined
    isClearable?: boolean
    isDisabled?: boolean
    isMulti?: boolean
    label?: string
    onChange: (v: MultiValue<SelectOptionData<string, MailMessageFolder>> | SingleValue<SelectOptionData<string, MailMessageFolder>>) => void
    query?: any,
    userId: string
    value: string | string[] | null
};

export const UserMailboxSelect = React.forwardRef<any, UserMailboxSelectProps>(({
    className,
    isClearable = false,
    isDisabled = false,
    isMulti = false,
    label = "Mailbox",
    onChange,
    query = useLazyFetchMailFoldersForUserQuery,
    userId,
    value
} : Readonly<UserMailboxSelectProps>, ref) => {
    const componentId = React.useId();
    const [cacheKey, setCacheKey] = React.useState<number>(new Date().valueOf());
    const [load, { data }] = query();

    const buildDefaultValue = () : SelectOptionData<string, MailMessageFolder> | SelectOptionData<string, MailMessageFolder>[] | null => {
        if (!value || !data) {
            return null;
        }

        const selectedValue = value;
        const results = data as MailMessageFolder[];

        const resultSets : SelectOptionData<string, MailMessageFolder>[] = [
            ...results.map((el) => ({ label: el.displayName, value: el.id, data: el, isDisabled: false } as SelectOptionData<string, MailMessageFolder>))
        ];

        if (resultSets.length === 0) {
            return null;
        }

        if (isMulti === true) {
            const selectedValues : string[] = Array.isArray(selectedValue) ? selectedValue : [selectedValue];
            return resultSets.filter(({ value }) => selectedValues.includes(value));
        }

        const foundResult = resultSets.find(({ value }) => value === selectedValue) ?? null;
        return foundResult;
    };

    const loadOptions = (input: string, callback: (options: SelectOptionData<string, MailMessageFolder>[]) => void) => {
        const newCacheKey = new Date().valueOf();
        load({ userId }).then(
            ({ data }: { data: MailMessageFolder[] }) => {
                if (data && data.length > 0) {
                    const records: SelectOptionData<string, MailMessageFolder>[] = data.map(el => ({
                        label: el.displayName,
                        value: el.id,
                        data: el,
                        isDisabled: false,
                        isFixed: false
                    } as SelectOptionData<string, MailMessageFolder>));
                    callback(records);
                }
                else {
                    callback([]);
                }
            }
        )
        .finally(() => {
            setCacheKey(() => newCacheKey);
        });
    };

    const displayValue = React.useMemo(() => buildDefaultValue(), [ data, isMulti, value ]);
    return (
        <FormGroup>
            <FormLabel htmlFor={componentId}>{label}</FormLabel>
            <FormInputGroup>
                <AsyncSelect
                    key={userId}
                    id={componentId}
                    className={classNames('flex-fill', className)}
                    ref={ref}
                    cacheOptions={true}
                    defaultOptions={true}
                    loadOptions={loadOptions}
                    isClearable={isClearable}
                    isDisabled={isDisabled}
                    isMulti={isMulti}
                    value={displayValue}
                    onChange={(e) => onChange(e)}
                />
            </FormInputGroup>
        </FormGroup>
    );
});